import { Button, Checkbox, Empty, Image, Input, Modal, Segmented, Select, Tag } from "antd";
import {
  BedDoubleIcon,
  BusIcon,
  CheckIcon,
  LayoutGridIcon,
  ListIcon,
  MapPinIcon,
  RefreshCwIcon,
  SearchIcon,
  TentTreeIcon,
  TicketCheckIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  resolvePortalTransportRecommendedActivities,
  useInfinitePortalInventoryQuery,
  type PortalInventoryItem,
  type PortalInventoryType,
} from "../actions/itinerary.actions";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

type PortalInventoryBrowserModalProps = {
  open: boolean;
  type: PortalInventoryType;
  destinations: string[];
  onClose: () => void;
  onSelect: (items: PortalInventoryItem[]) => void;
  onSelectWithLinkedActivities?: (
    items: PortalInventoryItem[],
    linkedActivities: Map<string, PortalInventoryItem[]>,
  ) => void;
  rateBasisFilter?: "daily" | "non_daily";
  fullDayMode?: boolean;
  /** `replace` selects exactly one item to swap into an existing service. */
  mode?: "add" | "replace";
};

const labels: Record<PortalInventoryType, { singular: string; plural: string }> = {
  hotel: { singular: "hotel", plural: "hotels" },
  activity: { singular: "activity", plural: "activities" },
  transport: { singular: "transport", plural: "transport options" },
};

const iconFor = {
  hotel: BedDoubleIcon,
  activity: TentTreeIcon,
  transport: BusIcon,
};

/** Presents rich, portal-safe inventory cards without exposing supplier or costing data. */
const PortalInventoryBrowserModal = ({
  open,
  type,
  destinations,
  onClose,
  onSelect,
  onSelectWithLinkedActivities,
  rateBasisFilter,
  fullDayMode = false,
  mode = "add",
}: PortalInventoryBrowserModalProps) => {
  const isReplacing = mode === "replace";
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [search, setSearch] = useState("");
  const [city, setCity] = useState<string>();
  const [category, setCategory] = useState<string>();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [linkedActivities, setLinkedActivities] = useState<Map<string, PortalInventoryItem[]>>(new Map());
  const [isLoadingLinkedActivities, setIsLoadingLinkedActivities] = useState(false);
  const [failedLinkedTransportIds, setFailedLinkedTransportIds] = useState<string[]>([]);
  const [linkedActivitiesRetryKey, setLinkedActivitiesRetryKey] = useState(0);
  const linkedActivitiesCacheRef = useRef<Map<string, PortalInventoryItem[]>>(new Map());
  const supportsLinkedActivities = !isReplacing && rateBasisFilter !== "daily" && Boolean(onSelectWithLinkedActivities);
  const debouncedSearch = useDebouncedValue(search, 500);
  const { items, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfinitePortalInventoryQuery(
    type,
    { countries: destinations, search: debouncedSearch, city, category: fullDayMode ? undefined : category, serviceType: fullDayMode ? "cab" : category },
    open,
  );
  const visibleItems = useMemo(
    () => items.filter((item) => type !== "transport" || !rateBasisFilter || (rateBasisFilter === "daily" ? item.rateBasis === "daily" : item.rateBasis !== "daily")),
    [items, rateBasisFilter, type],
  );
  const Icon = iconFor[type];
  const cities = useMemo(() => Array.from(new Set(visibleItems.map((item) => item.city).filter(Boolean))).sort(), [visibleItems]);
  const categories = useMemo(
    () =>
      Array.from(
        new Set(
          visibleItems.map((item) => item.category || item.serviceType).filter((value): value is string => Boolean(value)),
        ),
      ).sort(),
    [visibleItems],
  );
  useEffect(() => {
    if (!open) return;
    setSelectedIds([]);
    setLinkedActivities(new Map());
    setIsLoadingLinkedActivities(false);
    setFailedLinkedTransportIds([]);
    setLinkedActivitiesRetryKey(0);
    linkedActivitiesCacheRef.current.clear();
    setSearch("");
    setCity(undefined);
    setCategory(undefined);
  }, [open, type]);

  useEffect(() => {
    let cancelled = false;
    if (!open || type !== "transport" || !selectedIds.length || !supportsLinkedActivities) {
      setLinkedActivities(new Map());
      setIsLoadingLinkedActivities(false);
      setFailedLinkedTransportIds([]);
      return () => {
        cancelled = true;
      };
    }
    setFailedLinkedTransportIds((current) => {
      const remaining = current.filter((transportId) => selectedIds.includes(transportId));
      return remaining.length === current.length ? current : remaining;
    });
    const cachedItems = new Map<string, PortalInventoryItem[]>();
    selectedIds.forEach((transportId) => {
      const cached = linkedActivitiesCacheRef.current.get(transportId);
      if (cached) cachedItems.set(transportId, cached);
    });
    setLinkedActivities(cachedItems);
    const unresolvedIds = selectedIds.filter((transportId) => !linkedActivitiesCacheRef.current.has(transportId));
    if (!unresolvedIds.length) {
      setIsLoadingLinkedActivities(false);
      return () => {
        cancelled = true;
      };
    }
    setIsLoadingLinkedActivities(true);
    resolvePortalTransportRecommendedActivities(unresolvedIds)
      .then((result) => {
        if (cancelled) return;
        unresolvedIds.forEach((transportId) => {
          linkedActivitiesCacheRef.current.set(transportId, result.get(transportId) || []);
        });
        setLinkedActivities(
          new Map(
            selectedIds.map((transportId) => [transportId, linkedActivitiesCacheRef.current.get(transportId) || []]),
          ),
        );
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) {
          unresolvedIds.forEach((transportId) => linkedActivitiesCacheRef.current.set(transportId, []));
          setLinkedActivities(
            new Map(
              selectedIds.map((transportId) => [transportId, linkedActivitiesCacheRef.current.get(transportId) || []]),
            ),
          );
          setFailedLinkedTransportIds((current) => Array.from(new Set([...current, ...unresolvedIds])));
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingLinkedActivities(false);
      });
    return () => {
      cancelled = true;
    };
  }, [linkedActivitiesRetryKey, open, selectedIds, supportsLinkedActivities, type]);

  const linkedActivityCount = new Set(
    selectedIds.flatMap((transportId) => (linkedActivities.get(transportId) || []).map((activity) => activity.id)),
  ).size;
  const isResolvingLinkedActivities =
    selectedIds.length > 0 &&
    (isLoadingLinkedActivities || selectedIds.some((transportId) => !linkedActivities.has(transportId)));
  const linkedActivitiesButtonLabel =
    selectedIds.length === 0
      ? "Add with linked activities"
      : isResolvingLinkedActivities
        ? "Checking linked activities…"
        : linkedActivityCount > 0
          ? `Add with ${linkedActivityCount} linked ${linkedActivityCount === 1 ? "activity" : "activities"}`
          : "No linked activities";
  const canAddLinkedActivities = selectedIds.length > 0 && !isResolvingLinkedActivities && linkedActivityCount > 0;
  const selectedItems = visibleItems.filter((item) => selectedIds.includes(item.id));

  const toggleItem = (id: string) => {
    setSelectedIds((current) => {
      if (isReplacing) return current.includes(id) ? [] : [id];
      return current.includes(id) ? current.filter((selectedId) => selectedId !== id) : [...current, id];
    });
  };

  const retryLinkedActivities = () => {
    const failedIds = failedLinkedTransportIds.filter((transportId) => selectedIds.includes(transportId));
    failedIds.forEach((transportId) => linkedActivitiesCacheRef.current.delete(transportId));
    setFailedLinkedTransportIds([]);
    setLinkedActivities(
      new Map(
        selectedIds.flatMap((transportId) => {
          const cached = linkedActivitiesCacheRef.current.get(transportId);
          return cached ? [[transportId, cached] as const] : [];
        }),
      ),
    );
    setLinkedActivitiesRetryKey((current) => current + 1);
  };

  const loadMore = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (hasNextPage && !isFetchingNextPage && target.scrollHeight - target.scrollTop - target.clientHeight < 120) {
      fetchNextPage();
    }
  };

  return (
    <Modal
      open={open}
      width={920}
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <span className="block text-xs text-gray-500">{selectedIds.length} selected</span>
            {failedLinkedTransportIds.length > 0 && (
              <div role="alert" className="flex items-center gap-2 text-xs text-amber-700">
                <span>Could not check linked activities.</span>
                <Button
                  type="link"
                  size="small"
                  className="!h-auto !p-0 !text-amber-700"
                  onClick={retryLinkedActivities}
                >
                  Retry
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              disabled={selectedIds.length === 0}
              icon={isReplacing ? <RefreshCwIcon size={14} /> : <CheckIcon size={14} />}
              className={
                selectedIds.length > 0
                  ? "!border-(--brand-secondary) !bg-(--brand-secondary) !text-white"
                  : "!border-gray-300 !bg-white !text-gray-400"
              }
              onClick={() => {
                onSelect(selectedItems);
                onClose();
              }}
            >
              {isReplacing ? "Replace" : "Add selected"}
            </Button>
            {type === "transport" && supportsLinkedActivities ? (
              <div className="w-[250px]">
                <Button
                  block
                  disabled={!canAddLinkedActivities}
                  loading={isResolvingLinkedActivities}
                  icon={!isResolvingLinkedActivities ? <TicketCheckIcon size={14} /> : undefined}
                  aria-label={isResolvingLinkedActivities ? "Checking linked activities" : linkedActivitiesButtonLabel}
                  className={
                    canAddLinkedActivities
                      ? "!border-(--brand-secondary) !bg-(--brand-secondary) !text-[var(--brand-secondary-foreground)]"
                      : "!border-gray-300 !bg-white !text-gray-400"
                  }
                  onClick={() => {
                    onSelectWithLinkedActivities?.(selectedItems, linkedActivities);
                    onClose();
                  }}
                >
                  {linkedActivitiesButtonLabel}
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      }
      onCancel={onClose}
      destroyOnClose
      title={
        <div className="flex items-center gap-3 pr-8">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-(--brand-primary)/35 bg-(--brand-primary)/10">
            <Icon size={17} className="text-(--brand-primary)" strokeWidth={1.8} />
          </span>
          <div>
            <h2 className="text-base font-semibold text-[#131B2A]">
              {fullDayMode
                ? "Browse Full Day transports"
                : isReplacing
                  ? `Replace ${labels[type].singular}`
                  : `Browse ${labels[type].plural}`}
            </h2>
            <p className="text-xs font-normal text-gray-500">
              {destinations.length
                ? `Showing options for ${destinations.join(", ")}`
                : "Choose destinations to narrow inventory"}
            </p>
          </div>
        </div>
      }
    >
      <div className="mb-4 grid grid-cols-1 gap-2 rounded-lg border border-gray-200 bg-[#FBFBFB] p-2 sm:grid-cols-12">
        <Input
          allowClear
          prefix={<SearchIcon size={14} className="text-gray-400" />}
          placeholder={`Search ${labels[type].plural}`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={fullDayMode ? "sm:col-span-9" : "sm:col-span-6"}
        />
        <Select
          allowClear
          showSearch
          placeholder="All cities"
          value={city}
          onChange={setCity}
          options={cities.map((value) => ({ value, label: value }))}
          className="sm:col-span-3"
        />
        {!fullDayMode ? <Select
          allowClear
          showSearch
          placeholder={type === "transport" ? "All service types" : "All categories"}
          value={category}
          onChange={setCategory}
          options={categories.map((value) => ({ value, label: value.replace(/_/g, " ") }))}
          className="sm:col-span-3"
        /> : null}
      </div>
      <div className="mb-3 flex justify-end">
        <Segmented
          size="small"
          value={viewMode}
          onChange={(value) => setViewMode(value as "grid" | "list")}
          aria-label="Inventory layout"
          options={[
            { value: "grid", icon: <LayoutGridIcon size={13} />, label: "Grid" },
            { value: "list", icon: <ListIcon size={13} />, label: "List" },
          ]}
        />
      </div>

      {isLoading ? (
        <div className="grid max-h-[62vh] gap-3 overflow-y-auto pr-1 md:grid-cols-2" aria-label="Loading inventory">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-36 animate-pulse rounded-lg border border-gray-200 bg-gray-50" />
          ))}
        </div>
      ) : visibleItems.length ? (
        <div
          className={`grid max-h-[62vh] gap-3 overflow-y-auto pr-1 ${viewMode === "grid" ? "md:grid-cols-2" : "grid-cols-1"}`}
          onScroll={loadMore}
        >
          {visibleItems.map((item) => (
            <article
              key={item.id}
              role="button"
              tabIndex={0}
              aria-pressed={selectedIds.includes(item.id)}
              aria-label={`${selectedIds.includes(item.id) ? "Deselect" : "Select"} ${item.name}`}
              onClick={() => toggleItem(item.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleItem(item.id);
                }
              }}
              className={`cursor-pointer overflow-hidden rounded-lg border bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)] transition-[border-color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/40 ${
                selectedIds.includes(item.id)
                  ? "border-(--brand-primary) ring-2 ring-(--brand-primary)/15"
                  : "border-gray-200 hover:border-(--brand-primary)/70 hover:shadow-[0_8px_24px_rgba(19,27,42,0.08)]"
              }`}
            >
              <div className="flex gap-3 p-3">
                <div className="shrink-0">
                  <Image
                    width={112}
                    height={92}
                    preview={false}
                    src={item.image || undefined}
                    fallback={type === "activity" ? "/images/activity.jpg" : "/images/no-image.jpg"}
                    className="rounded-lg border border-gray-200 object-cover"
                    alt={item.name}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-semibold text-[#131B2A]">{item.name}</h3>
                  <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                    <MapPinIcon size={12} /> {item.city}, {item.country}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {item.category ? <Tag className="!m-0">{item.category}</Tag> : null}
                    {item.serviceType ? <Tag className="!m-0">{item.serviceType.replace(/_/g, " ")}</Tag> : null}
                    {item.duration ? <Tag className="!m-0">{item.duration} hrs</Tag> : null}
                  </div>
                </div>
              </div>
              {item.description ? (
                <p className="line-clamp-2 px-3 text-xs leading-5 text-gray-600">{item.description}</p>
              ) : null}
              <div className="mt-3 flex items-center justify-between border-t border-gray-200 px-3 py-2.5">
                <span className="text-[11px] text-gray-500">
                  {item.roomOptions?.length
                    ? `${item.roomOptions.length} room option${item.roomOptions.length === 1 ? "" : "s"}`
                    : item.vehicleOptions?.length
                      ? `${item.vehicleOptions.length} vehicle option${item.vehicleOptions.length === 1 ? "" : "s"}`
                      : "View product details"}
                </span>
                <Checkbox
                  checked={selectedIds.includes(item.id)}
                  onClick={(event) => event.stopPropagation()}
                  onChange={() => toggleItem(item.id)}
                >
                  Select {labels[type].singular}
                </Checkbox>
              </div>
            </article>
          ))}
          {isFetchingNextPage &&
            Array.from({ length: 2 }).map((_, index) => (
              <div key={`more-${index}`} className="h-36 animate-pulse rounded-lg border border-gray-200 bg-gray-50" />
            ))}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${labels[type].plural} match these filters`} />
      )}
    </Modal>
  );
};

export default PortalInventoryBrowserModal;
